import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MaterialSymbol, { toolbarIconButtonSx } from '../components/schedules/MaterialSymbol';
import {
  listSchedulesProjects,
  type ScheduleProjectSummary,
} from '../services/schedulesService';

/**
 * Główna strona modułu „Harmonogramy" (`/app/schedules`).
 *
 * Pokazuje listę projektów podzieloną na dwa taby: „W realizacji" / „Zakończone".
 * Wejście w projekt otwiera listę harmonogramów tego projektu.
 *
 * Layout dopasowany do EasyEstimate — maksymalnie płaski, z paskiem narzędzi nad
 * tabelą (ikony po lewej, wyszukiwanie po prawej).
 */

type TabKey = 'active' | 'finished';

const formatDate = (s: string | null) => (s ? s : '—');

function phaseIsFinished(phase: string): boolean {
  return /^ZAKOŃCZONE/.test(phase || '') || phase === 'ARCHIWALNY';
}

const SchedulesListPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(true);
  const [projects, setProjects] = React.useState<ScheduleProjectSummary[]>([]);
  const [tab, setTab] = React.useState<TabKey>('active');
  const [search, setSearch] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [reloadTick, setReloadTick] = React.useState(0);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const rows = await listSchedulesProjects();
        if (!cancelled) setProjects(rows);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Błąd wczytywania');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadTick]);

  const { activeList, finishedList } = React.useMemo(() => {
    const active: ScheduleProjectSummary[] = [];
    const finished: ScheduleProjectSummary[] = [];
    for (const p of projects) {
      if (phaseIsFinished(p.project_phase)) finished.push(p);
      else active.push(p);
    }
    /* Sortowanie: najpierw te z harmonogramami (świeżo edytowane), potem reszta alfabetycznie. */
    const sortFn = (a: ScheduleProjectSummary, b: ScheduleProjectSummary) => {
      const aHas = a.schedules_count > 0 ? 1 : 0;
      const bHas = b.schedules_count > 0 ? 1 : 0;
      if (aHas !== bHas) return bHas - aHas;
      const aTime = a.last_schedule_updated_at ? new Date(a.last_schedule_updated_at).getTime() : 0;
      const bTime = b.last_schedule_updated_at ? new Date(b.last_schedule_updated_at).getTime() : 0;
      if (aTime !== bTime) return bTime - aTime;
      return a.project_name.localeCompare(b.project_name, 'pl');
    };
    active.sort(sortFn);
    finished.sort(sortFn);
    return { activeList: active, finishedList: finished };
  }, [projects]);

  const rows = tab === 'active' ? activeList : finishedList;
  const filteredRows = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        (r.project_id || '').toLowerCase().includes(q) ||
        (r.project_name || '').toLowerCase().includes(q) ||
        (r.client_name || '').toLowerCase().includes(q)
    );
  }, [rows, search]);

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        bgcolor: '#FAFAFA',
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: 1.5,
          py: 1,
          borderBottom: '1px solid #e8eaed',
          bgcolor: '#FFFFFF',
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: '18px',
            fontWeight: 500,
            color: '#020242',
          }}
        >
          Harmonogramy
        </Typography>
        <Tooltip title="Odśwież">
          <IconButton size="small" onClick={() => setReloadTick((n) => n + 1)} sx={toolbarIconButtonSx}>
            <MaterialSymbol name="refresh" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: 1.5,
          borderBottom: '1px solid #e8eaed',
          bgcolor: '#FFFFFF',
          minHeight: 42,
        }}
      >
        <Tabs
          value={tab}
          onChange={(_e, v) => setTab(v as TabKey)}
          textColor="inherit"
          sx={{
            minHeight: 42,
            '& .MuiTab-root': {
              minHeight: 42,
              fontFamily: 'Roboto, sans-serif',
              fontSize: 14,
              textTransform: 'none',
              fontWeight: 500,
            },
            '& .Mui-selected': { color: '#020242' },
            '& .MuiTabs-indicator': { backgroundColor: '#020242', height: 3 },
          }}
        >
          <Tab
            value="active"
            label={`W realizacji (${activeList.length})`}
          />
          <Tab
            value="finished"
            label={`Zakończone (${finishedList.length})`}
          />
        </Tabs>
        <TextField
          size="small"
          variant="outlined"
          placeholder="Szukaj projektu"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{
            width: 260,
            fontFamily: 'Roboto, sans-serif',
            '& .MuiOutlinedInput-root': { height: 32, fontSize: 14 },
            '& .MuiOutlinedInput-input': { py: 0 },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#595966', fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 1.5 }}>
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : error ? (
          <Box sx={{ p: 2, color: '#c62828', fontFamily: 'Roboto, sans-serif' }}>{error}</Box>
        ) : filteredRows.length === 0 ? (
          <Box
            sx={{
              p: 4,
              textAlign: 'center',
              color: '#5F6368',
              fontFamily: 'Roboto, sans-serif',
              bgcolor: '#FFFFFF',
              border: '1px dashed #dadce0',
              borderRadius: 1,
            }}
          >
            Brak projektów w tej zakładce.
          </Box>
        ) : (
          <Box
            sx={{
              bgcolor: '#FFFFFF',
              border: '1px solid #e8eaed',
              borderRadius: 1,
              overflow: 'hidden',
              fontFamily: 'Roboto, sans-serif',
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '180px 2fr 1fr 120px 200px 120px',
                px: 1,
                py: 0.75,
                bgcolor: '#F1F3F4',
                borderBottom: '1px solid #dadce0',
                fontSize: 12,
                fontWeight: 600,
                color: '#3c4043',
              }}
            >
              <Box>ID projektu</Box>
              <Box>Projekt</Box>
              <Box>Klient</Box>
              <Box sx={{ textAlign: 'right' }}>Harmonogramów</Box>
              <Box>Ostatnia aktualizacja</Box>
              <Box>Termin</Box>
            </Box>
            {filteredRows.map((p) => (
              <Box
                key={p.project_id}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '180px 2fr 1fr 120px 200px 120px',
                  px: 1,
                  py: 1,
                  borderBottom: '1px solid #f1f3f4',
                  fontSize: 13,
                  alignItems: 'center',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: '#EEF3FF' },
                }}
                onClick={() =>
                  navigate(`/app/schedules/project/${encodeURIComponent(p.project_id)}`)
                }
              >
                <Box
                  sx={{
                    color: '#5F6368',
                    fontFamily: '"Roboto Mono", monospace',
                    fontSize: 12,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {p.project_id}
                </Box>
                <Box sx={{ fontWeight: 500, color: '#202124', minWidth: 0 }}>
                  <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {p.project_name}
                  </Box>
                  <Box sx={{ fontSize: 11, color: '#9AA0A6' }}>{p.project_phase}</Box>
                </Box>
                <Box
                  sx={{
                    color: '#5F6368',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {p.client_name || '—'}
                </Box>
                <Box sx={{ textAlign: 'right', fontWeight: p.schedules_count > 0 ? 600 : 400 }}>
                  {p.schedules_count}
                </Box>
                <Box sx={{ color: '#5F6368', fontSize: 12 }}>
                  {p.last_schedule_updated_at
                    ? new Date(p.last_schedule_updated_at).toLocaleString('pl-PL')
                    : '—'}
                </Box>
                <Box sx={{ color: '#5F6368', fontSize: 12 }}>{formatDate(p.end_date)}</Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SchedulesListPage;
